import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { GlobalAccount, GUID, SettlementType } from '@ndvr/billing-api';

import { FormLabel, FormRadioButton, VerticalLayout } from 'widgets';

import { PortfolioSelect } from '../widgets/PortfolioSelect';

const settlementOptions = [
    { type: SettlementType.DEFAULT_SETTLEMENT },
    { type: SettlementType.SETTLED_BY_PORTFOLIO },
    { type: SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO, disabled: true },
];

interface Props {
    names: {
        type: string;
        settlementPortfolioGUID: string;
    };
    globalAccountGUID: GUID<GlobalAccount>;
    defaultSettlementDescription: string;
    disabled?: boolean;
    title?: string;
}

export const SettlementSelect = ({
    names,
    globalAccountGUID,
    defaultSettlementDescription,
    disabled,
    title,
}: Props) => {
    const { watch, setValue } = useFormContext();
    const portfolioGUID = watch('portfolioGUID');
    const selectedType = watch(names.type);

    useEffect(() => {
        if (selectedType === SettlementType.DEFAULT_SETTLEMENT) {
            setValue(names.settlementPortfolioGUID, undefined);
        }
    }, [names.settlementPortfolioGUID, selectedType, setValue]);

    return (
        <VerticalLayout $gap={8}>
            <FormLabel as="span">{title}</FormLabel>
            <VerticalLayout $gap={16}>
                {settlementOptions.map((option) => (
                    <VerticalLayout $gap={4} key={option.type}>
                        <FormRadioButton
                            name={names.type}
                            disabled={option.disabled || disabled}
                            value={option.type}
                            label={getSettlementTypeLabel(option.type, defaultSettlementDescription)}
                        />
                        {option.type === selectedType && option.type === SettlementType.SETTLED_BY_PORTFOLIO && (
                            <VerticalLayout $margin="0 0 0 24px">
                                <PortfolioSelect
                                    name={names.settlementPortfolioGUID}
                                    globalAccountGUID={globalAccountGUID}
                                    excludedPortfolios={[portfolioGUID]}
                                    disabled={disabled}
                                    hideLabel
                                />
                            </VerticalLayout>
                        )}
                    </VerticalLayout>
                ))}
            </VerticalLayout>
        </VerticalLayout>
    );
};

const getSettlementTypeLabel = (type: SettlementType, defaultSettlementDescription: string): string => {
    switch (type) {
        case SettlementType.DEFAULT_SETTLEMENT:
            return `Default allocation (${defaultSettlementDescription})`;
        case SettlementType.SETTLED_BY_PORTFOLIO:
            return 'Select settlement Account';
        case SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO:
            return 'External settlement Account';
        default:
            return 'Unknown method';
    }
};
